import type { MetaAd } from '@storefronts/common-types'

export type MetaAdSortKey =
    | 'spend'
    | 'name'
    | 'ctr'
    | 'roas'
    | 'totalPurchasesValue'
    | 'conversionRate'

export const META_AD_SORT_OPTIONS: Array<{
    key: MetaAdSortKey
    label: string
}> = [
    { key: 'spend', label: 'Spend' },
    { key: 'name', label: 'Name' },
    { key: 'ctr', label: 'CTR' },
    { key: 'roas', label: 'ROAS' },
    { key: 'totalPurchasesValue', label: 'Revenue' },
    { key: 'conversionRate', label: 'Conversion rate' },
]

export type MetaAdSortDirection = 'ascending' | 'descending'

export interface MetaAdSortConfig {
    key: MetaAdSortKey
    direction: MetaAdSortDirection
}

const NUMERIC_SORT_KEYS = new Set<MetaAdSortKey>([
    'spend',
    'roas',
    'ctr',
    'conversionRate',
    'totalPurchasesValue',
])

export function parseMetaAdMetric(value: string | number | undefined): number {
    if (value === undefined) return NaN
    if (typeof value === 'number') return value
    return Number(value.replace(/[^\d.-]/g, ''))
}

export function sortMetaAds(
    ads: MetaAd[],
    sortConfig: MetaAdSortConfig | null,
    pinnedAd?: MetaAd,
): MetaAd[] {
    const sortableData = [...ads]
    const preselectedAd = pinnedAd ?? undefined

    if (preselectedAd) {
        const pinnedIndex = sortableData.findIndex(
            (ad) => ad.id === preselectedAd.id,
        )
        if (pinnedIndex >= 0) {
            sortableData.splice(pinnedIndex, 1)
        }
    }

    if (sortConfig) {
        sortableData.sort((a, b) => {
            let aValue: string | number | undefined
            let bValue: string | number | undefined

            if (NUMERIC_SORT_KEYS.has(sortConfig.key)) {
                aValue = parseMetaAdMetric(a[sortConfig.key] as string)
                bValue = parseMetaAdMetric(b[sortConfig.key] as string)
            } else {
                aValue = a[sortConfig.key]
                bValue = b[sortConfig.key]
            }

            if (aValue === undefined && bValue === undefined) return 0
            if (aValue === undefined) return 1
            if (bValue === undefined) return -1

            if (aValue === bValue) return 0

            if (typeof aValue === 'number' && Number.isNaN(aValue)) return 1
            if (typeof bValue === 'number' && Number.isNaN(bValue)) return -1

            if (sortConfig.direction === 'ascending') {
                return aValue < bValue ? -1 : 1
            }
            return aValue > bValue ? -1 : 1
        })
    }

    if (preselectedAd) {
        sortableData.unshift(preselectedAd)
    }

    return sortableData
}

export function metaAdHasCreative(ad: MetaAd): boolean {
    return Boolean(ad.thumbnailUrl || ad.mediaUrl || ad.creativeImageUrl)
}

export function findMetaAdMatchingNameParam(
    ads: MetaAd[],
    adName: string | null,
): MetaAd | undefined {
    if (!adName) {
        return undefined
    }

    return ads.find((ad) => ad.name === adName || ad.adId === adName)
}
